import { useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const MenuItem = ({ item, testid, onClick, compact = false }) => {
  const { icon: I, title, desc } = item;
  const cls = `group flex items-start gap-3 rounded-2xl transition-colors duration-200 hover:bg-[#EAF3FF] ${compact ? "px-4 py-2" : "p-3"}`;
  const body = (
    <>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#EAF3FF] text-[#0B2A6F] transition-colors duration-200 group-hover:bg-[#0B2A6F] group-hover:text-lime-300"><I className="h-4 w-4" /></span>
      <span className="min-w-0">
        <span className="block text-sm font-bold text-[#050B1F]">{title}</span>
        <span className="block text-xs leading-snug text-slate-600">{desc}</span>
      </span>
    </>
  );
  return item.href
    ? <a href={item.href} onClick={onClick} data-testid={testid} className={cls}>{body}</a>
    : <Link to={item.to} onClick={onClick} data-testid={testid} className={cls}>{body}</Link>;
};

export const NavDropdown = ({ menu }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const id = slug(menu.label);
  const show = () => { clearTimeout(timer.current); setOpen(true); };
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 140); };
  const linkCls = ({ isActive }) => `inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300 ${isActive ? "bg-[#EAF3FF] text-[#0B2A6F]" : "text-slate-700 hover:text-[#0B2A6F]"}`;

  if (!menu.items) {
    return <NavLink to={menu.to} end={menu.to === "/"} data-testid={`nav-link-${id}`} className={linkCls}>{menu.label}</NavLink>;
  }
  const wide = menu.items.length > 4;
  return (
    <div className="relative" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      <NavLink to={menu.to} end={menu.to === "/"} data-testid={`nav-link-${id}`} aria-expanded={open} className={linkCls}>
        {menu.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 10, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.18 }} data-testid={`nav-dropdown-${id}`}
            className={`absolute left-1/2 top-full z-[60] -translate-x-1/2 pt-3 ${wide ? "w-[620px]" : "w-[340px]"}`}>
            <div className="rounded-3xl border border-[#0B2A6F]/10 bg-white p-3 shadow-[0_30px_80px_-20px_rgba(5,11,31,0.45)]">
              <div className={`grid gap-1 ${wide ? "grid-cols-2" : ""}`}>
                {menu.items.map((it) => <MenuItem key={it.title} item={it} testid={`nav-dd-${id}-${slug(it.title)}`} onClick={() => setOpen(false)} />)}
              </div>
              <Link to={menu.to} onClick={() => setOpen(false)} data-testid={`nav-dd-${id}-page`}
                className="mt-2 flex items-center justify-between rounded-2xl bg-[#0B2A6F] px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-[#050B1F]">
                Go to {menu.label} <ArrowRight className="h-4 w-4 text-lime-300" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const MobileMenuGroup = ({ menu, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const id = slug(menu.label);
  return (
    <div>
      <div className="flex items-center">
        <Link to={menu.to} onClick={onNavigate} data-testid={`mobile-link-${id}`}
          className="flex-1 rounded-2xl px-4 py-3 font-display text-2xl font-bold text-[#050B1F] hover:bg-[#EAF3FF]">{menu.label}</Link>
        {menu.items && (
          <button onClick={() => setOpen((v) => !v)} data-testid={`mobile-expand-${id}`} aria-label={`Show ${menu.label} sections`}
            className="rounded-full p-3 text-[#0B2A6F] hover:bg-[#EAF3FF]">
            <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>
      {open && menu.items && (
        <div className="pb-2" data-testid={`mobile-submenu-${id}`}>
          {menu.items.map((it) => <MenuItem key={it.title} item={it} compact testid={`mobile-dd-${id}-${slug(it.title)}`} onClick={onNavigate} />)}
        </div>
      )}
    </div>
  );
};
